import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';

// O outro lado dos webhooks: o que a META mandou para o bZapper, cru, antes de
// virar evento do bZapper. Junto do inspector (o que chega ao seu sistema), dá
// para ver a cadeia inteira — e, quando algo não chega, em qual ponta parou.

interface MetaEvent {
  id: number;
  received_at: string;
  fields: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  attempts: number;
  error?: string;
  body: unknown;
}

const STATUS_VARIANT: Record<MetaEvent['status'], 'success' | 'secondary' | 'destructive' | 'warning'> = {
  done: 'success', pending: 'warning', running: 'secondary', failed: 'destructive',
};

export function MetaEventsPanel() {
  const t = useT();
  const [open, setOpen] = useState<number | null>(null);
  const { data: list = [], refetch, isFetching, isError } = useQuery({
    queryKey: ['meta-webhook-events'],
    queryFn: async (): Promise<MetaEvent[]> => {
      const r = await apiFetch('GET', '/official/webhook-events?limit=50');
      if (!r.ok) throw new Error('meta-events');
      return (r.data as { data?: MetaEvent[] })?.data ?? [];
    },
    refetchInterval: 4000,
  });

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold">{t('playground.meta.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('playground.meta.subtitle')}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            <RefreshCw className={'size-4 ' + (isFetching ? 'animate-spin' : '')} />
          </Button>
        </div>
        {isError ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('playground.meta.error')}</p>
        ) : list.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('playground.meta.empty')}</p>
        ) : (
          <ul className="flex flex-col divide-y rounded-md border">
            {list.map((ev) => (
              <li key={ev.id}>
                <button onClick={() => setOpen(open === ev.id ? null : ev.id)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/50">
                  <ChevronRight className={'size-4 shrink-0 text-muted-foreground transition-transform ' + (open === ev.id ? 'rotate-90' : '')} />
                  <Badge variant="secondary" className="font-mono">{ev.fields || '—'}</Badge>
                  <Badge variant={STATUS_VARIANT[ev.status]}>{t(`playground.meta.status.${ev.status}`)}</Badge>
                  <span className="ml-auto text-xs text-muted-foreground">{new Date(ev.received_at).toLocaleTimeString()}</span>
                </button>
                {open === ev.id && (
                  <div className="border-t bg-muted/30 px-4 py-3">
                    {ev.error && <p className="mb-2 text-xs text-destructive">{ev.error}</p>}
                    <pre className="overflow-x-auto text-xs leading-relaxed"><code>{JSON.stringify(ev.body, null, 2)}</code></pre>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
